<template>
  <div class="image-upload-container">
    <label v-if="label" class="form-label">{{ label }}</label>
    <a-upload
      :file-list="fileList"
      :max-count="1"
      :multiple="false"
      list-type="picture-card"
      :show-upload-list="{ showPreviewIcon: false, showRemoveIcon: true }"
      :before-upload="handleBeforeUpload"
      @change="handleChange"
      :custom-request="customRequest"
      :accept="acceptImageTypes"
    >
      <div v-if="fileList.length === 0">
        <plus-outlined />
        <div style="margin-top: 8px">업로드</div>
      </div>
    </a-upload>
    <div class="upload-info" v-if="fileList.length === 0">
      이미지 형식: JPG, PNG, GIF, SVG, WebP (최대 {{ maxSize }}MB)
    </div>
    <div v-if="uploadStatus === 'uploading'" class="upload-loading">
      <a-spin size="small" />
      <span class="upload-loading-text">업로드 중...</span>
    </div>
    <div v-if="uploadStatus === 'error'" class="upload-error">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import { PlusOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';

export default {
  name: 'ImageUpload',
  components: {
    PlusOutlined
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    maxSize: {
      type: Number, 
      default: 2 // 2MB
    },
    uploadPath: {
      type: String,
      default: ''
    },
    containerName: {
      type: String,
      default: 'certilife'
    },
    autoUpload: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      fileList: [],
      imageUrl: '',
      uploadStatus: '', // 'uploading', 'success', 'error', ''
      errorMessage: '',
      acceptImageTypes: 'image/jpeg,image/png,image/gif,image/svg+xml,image/webp'
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (!newValue) {
          this.imageUrl = '';
          this.fileList = [];
          return;
        }
        
        if (typeof newValue === 'string') {
          this.imageUrl = newValue;
          this.fileList = [{
            uid: `-${Date.now()}`,
            name: '이미지',
            status: 'done',
            url: newValue,
            thumbUrl: newValue,
            size: 0,
            type: 'image/jpeg'
          }];
        }
      }
    }
  },
  methods: {
    // SVG 및 WebP 파일 처리 도우미
    isSupportedImageType(fileType) {
      if (!fileType) return false;
      
      const supportedTypes = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/svg+xml',
        'image/webp'
      ];
      
      return supportedTypes.includes(fileType);
    },
    
    getImageMimeType(fileName) {
      if (!fileName) return '';
      
      const extension = fileName.split('.').pop().toLowerCase();
      
      const mimeTypes = {
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'gif': 'image/gif',
        'svg': 'image/svg+xml',
        'webp': 'image/webp'
      };
      
      return mimeTypes[extension] || '';
    },

    // Ant Design 커스텀 업로드 요청 처리
    customRequest({ file, onSuccess, onError, onProgress }) {
      // 자동 업로드가 아닌 경우 성공 처리하고 종료
      if (!this.autoUpload) {
        onSuccess('success', file);
        return;
      }
      
      // 업로드 상태 설정
      this.uploadStatus = 'uploading';
      this.errorMessage = '';
      
      // 초기 진행률 설정
      onProgress({ percent: 0 });
      
      // 안전하게 파일 처리
      try {
        // SVG 및 기타 특수 이미지 파일 타입 확인
        const isSvg = file.type === 'image/svg+xml';
        const isWebp = file.type === 'image/webp';
        
        // 파일 미리보기 생성
        const reader = new FileReader();
        
        // 파일 읽기 완료 처리
        reader.onload = () => {
          try {
            const result = reader.result;
            if (!result || typeof result !== 'string') {
              throw new Error('파일을 읽을 수 없습니다.');
            }
            
            // 미리보기 이미지 설정
            this.imageUrl = result;
            
            // 초기 진행률 표시
            onProgress({ percent: 10 });
            
            // 폼 데이터 생성
            const formData = new FormData();
            formData.append('container', (this.containerName || '').trim());
            formData.append('files', file);
            
            // SVG 및 WEBP를 위한 추가 파라미터
            if (isSvg || isWebp) {
              formData.append('allowSpecialFormats', 'true');
            }
            
            // API URL 생성
            const url = '/object-storages/azure/files/upload';
            const queryParams = new URLSearchParams();
            
            // 업로드 경로가 있으면 추가
            if (this.uploadPath) {
              queryParams.append('folderPath', this.uploadPath);
            }
            
            // 완성된 URL
            const fullUrl = `${url}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
            
            // API 요청 전송
            this.$apiInstance.post(fullUrl, formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
                'X-Azure-Container': this.containerName,
                'X-Allow-Special-Formats': isSvg || isWebp ? 'true' : 'false'
              },
              onUploadProgress: progressEvent => {
                // 진행률 계산 및 표시 (10%~90%)
                const percent = Math.min(
                  90, 
                  10 + Math.round((progressEvent.loaded * 80) / Math.max(1, progressEvent.total))
                );
                onProgress({ percent });
              }
            }).then(response => {
              try {
                // 응답 데이터 확인
                if (!response || !response.data) {
                  throw new Error('서버 응답이 없습니다.');
                }
                
                // 성공 응답 처리
                if (response.data.success) {
                  // 응답 데이터에서 URL 추출
                  if (response.data.data && 
                      Array.isArray(response.data.data.results) && 
                      response.data.data.results.length > 0 && 
                      response.data.data.results[0].url) {
                    
                    const uploadedUrl = response.data.data.results[0].url;

                    
                    // 업로드 성공 상태로 변경
                    this.uploadStatus = 'success';
                    
                    // 이미지 URL 업데이트 및 이벤트 발생
                    this.imageUrl = uploadedUrl;
                    this.$emit('update:value', uploadedUrl);
                    this.$emit('change', uploadedUrl, file);
                    this.$emit('upload-success', response.data);
                    
                    // 파일 목록 업데이트
                    this.fileList = [{
                      uid: file.uid || `-${Date.now()}`,
                      name: file.name || '이미지',
                      status: 'done',
                      url: uploadedUrl,
                      thumbUrl: uploadedUrl,
                      size: file.size || 0,
                      type: file.type || 'image/jpeg',
                      percent: 100
                    }];
                    
                    // 완료 처리
                    onProgress({ percent: 100 });
                    onSuccess(response.data, file);
                    message.success('이미지가 성공적으로 업로드되었습니다.');
                  } else {
                    throw new Error('업로드된 URL이 없습니다.');
                  }
                } else {
                  throw new Error(response.data.error || '업로드 실패');
                }
              } catch (parseError) {
                console.error('응답 처리 오류:', parseError);
                this.uploadStatus = 'error';
                this.errorMessage = parseError.message || '응답 처리 실패';
                onError(parseError);
                message.error(`업로드 실패: ${this.errorMessage}`);
              }
            }).catch(error => {
              console.error('업로드 오류:', error);
              this.uploadStatus = 'error';
              this.errorMessage = error.message || '알 수 없는 오류';
              this.$emit('upload-error', error);
              onError(error);
              message.error(`업로드 실패: ${this.errorMessage}`);
            });
          } catch (processError) {
            console.error('파일 처리 오류:', processError);
            this.uploadStatus = 'error';
            this.errorMessage = processError.message || '파일 처리 실패';
            onError(processError);
            message.error(`파일 처리 실패: ${this.errorMessage}`);
          }
        };
        
        // 파일 읽기 오류 처리
        reader.onerror = () => {
          console.error('파일 읽기 오류');
          this.uploadStatus = 'error';
          this.errorMessage = '파일 읽기 실패';
          onError(new Error('파일 읽기 실패'));
          message.error('파일 읽기에 실패했습니다.');
        };
        
        // 파일 읽기 시작
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('업로드 처리 오류:', error);
        this.uploadStatus = 'error';
        this.errorMessage = error.message || '알 수 없는 오류';
        onError(error);
        message.error(`업로드 처리 실패: ${this.errorMessage}`);
      }
    },

    // 업로드 전 파일 검증
    handleBeforeUpload(file) {
      // 파일 존재 여부 확인
      if (!file) {
        message.error('유효하지 않은 파일입니다.');
        return false;
      }
      
      // 이미지 타입 확인
      const isImage = this.isSupportedImageType(file.type);
      
      if (!isImage) {
        message.error('지원되는 이미지 형식: JPG, PNG, GIF, SVG, WebP');
        return false;
      }
      
      // 파일 크기 확인
      const isLtMaxSize = file.size && file.size / 1024 / 1024 < this.maxSize;
      if (!isLtMaxSize) {
        message.error(`이미지 크기는 ${this.maxSize}MB 이하여야 합니다!`);
        return false;
      }
      
      // 자동 업로드가 아닌 경우, 수동으로 파일 처리
      if (!this.autoUpload) {
        try {
          const reader = new FileReader();
          
          reader.onload = () => {
            const result = reader.result;
            if (typeof result === 'string') {
              // 미리보기 이미지 설정
              this.imageUrl = result;
              
              // 이벤트 발생
              this.$emit('update:value', result);
              this.$emit('change', result, file);
              
              // 파일 목록 설정 (업로드하지 않음)
              this.fileList = [{
                uid: file.uid || `-${Date.now()}`,
                name: file.name || '이미지',
                status: 'done',
                url: result,
                thumbUrl: result,
                size: file.size || 0,
                type: file.type || 'image/jpeg'
              }];
            }
          };
          
          reader.onerror = () => {
            message.error('파일 읽기에 실패했습니다.');
          };
          
          // 파일 읽기 시작
          reader.readAsDataURL(file);
        } catch (error) {
          console.error('파일 처리 오류:', error);
          message.error('파일 처리에 실패했습니다.');
        }
        
        return false; // 기본 업로드 처리 방지
      }
      
      // customRequest 사용 허용
      return true;
    },

    // 이미지 변경 처리
    handleChange(info) {
      // 정보가 없는 경우 처리하지 않음
      if (!info) return;
      
      try {
        // 파일 상태 로깅
        if (info.file) {
          console.log('ImageUpload handleChange:', info.file.status || '상태 없음');
        }
        
        // 파일 및 상태 확인
        const file = info.file;
        if (!file) return;
        
        // 파일 상태별 처리
        if (file.status === 'removed') {
          // 파일 제거 시 모든 상태 초기화
          this.imageUrl = '';
          this.fileList = [];
          this.uploadStatus = '';
          this.errorMessage = '';
          
          // 이벤트 발생
          this.$emit('update:value', '');
          this.$emit('change', '', null);
        } else if (file.status === 'error') {
          // 업로드 오류 처리
          this.uploadStatus = 'error';
          this.errorMessage = file.response?.message || '파일 업로드 실패';
          message.error('파일 업로드에 실패했습니다.');
        }
      } catch (error) {
        console.error('이미지 변경 처리 오류:', error);
      }
    }
  }
};
</script>

<style scoped>
.image-upload-container {
  margin-bottom: 16px;
  position: relative;
}

.image-upload-container .form-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 700;

}

.upload-info {
  font-size: 12px;
  color: #888;
  margin-top: 4px;
  margin-bottom: 8px;
}

.image-upload-container .upload-loading {
  margin-top: 8px;
  display: flex;
  align-items: center;
}

.image-upload-container .upload-loading-text {
  margin-left: 8px;
  color: #1890ff;
}

.image-upload-container .upload-error {
  margin-top: 8px;
  color: #ff4d4f;
  font-size: 12px;
}

:deep(.ant-upload-list-item-actions) {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style> 